/**
 * Historique local des sessions d'entraînement (localStorage, par pseudo).
 * Permet de reprendre une partie en cours ou de consulter les parties terminées.
 */

import type { TrainingAIModelSelection, TrainingDisplayMode } from '../types/app';
import type { CompareReplayDealEntry } from './competitionReplay';

export type TrainingHistoryGameSpeed = 'fast' | 'normal' | 'slow' | 'click';

const STORAGE_KEY_PREFIX = 'wt_neo_training_history';
const MAX_ENTRIES = 40;

export type TrainingHistoryStatus = 'in_progress' | 'finished';

export type TrainingHistoryFilter = 'in_progress' | 'finished';

export type TrainingHistorySource =
  | 'solo'
  | 'library'
  | 'differe'
  | 'compare'
  | 'poll'
  | 'cia'
  | 'team_champ'
  | 'other';

export type TrainingHistoryContextKind = TrainingHistorySource;

export interface TrainingHistoryContext {
  kind: TrainingHistoryContextKind;
  library?: {
    gameId?: number | null;
    source?: 'own' | 'shared';
    sharedBy?: string;
    originType?: string;
    originId?: string;
    originDonneNum?: number;
    note?: string;
    contractLabel?: string;
    tags?: string[];
    isPartial?: boolean;
  };
  differe?: {
    differeId?: number | string;
    differeType?: string;
    subType?: string;
    dateStart?: string;
    dateEnd?: string;
    pseudo?: string;
    pseudoDisplay?: string;
    dealNum?: number;
    partieId?: number;
    statsDrill?: boolean;
    /** Exercice « tes erreurs » (bifurcation différé). */
    exerciseMistake?: boolean;
  };
  cia?: {
    opponentDisplay?: string;
    player1Display?: string;
    player2Display?: string;
    player1Pseudo?: string;
    player2Pseudo?: string;
    viewerPseudo?: string;
    matchId?: number;
    dealNum?: number;
    tableIdx?: number;
  };
  teamChamp?: {
    matchId?: number;
    equipe1Nom?: string;
    equipe2Nom?: string;
    equipe1Id?: number;
    equipe2Id?: number;
    /** Date du match (ISO ou date SQL), pas la date de consultation. */
    matchDate?: string;
    phaseId?: number;
    divisionId?: number;
    dealNum?: number;
    tableIdx?: number;
  };
  compare?: {
    metric?: string;
    metricLabel?: string;
    modelLabel?: string;
    dealCount?: number;
    selectedDealIndex?: number;
    tableIdx?: number;
  };
  poll?: {
    sharedGameId?: number;
    sharedBy?: string;
  };
}

export const DIFFERE_SUB_TYPE_LABELS: Record<string, string> = {
  mixte: 'Mixte',
  normal: 'Normal',
  maxi: 'Maxi',
  attaque: 'Attaque',
  defense: 'Défense',
  attaqueSC: 'Attaque SC',
  maxiAtt: 'Maxi Attaque',
  maxiDef: 'Maxi Défense',
};

export const DIFFERE_CATEGORY_LABELS: Record<string, string> = {
  indiv: 'Individuel',
  tripl: 'Triplette',
  quad: 'Équipe',
};

function formatDiffereHistoryDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return dateStr;
  }
}

export function formatDiffereDateRange(dateStart?: string, dateEnd?: string): string | undefined {
  const finStr = dateEnd ? formatDiffereHistoryDate(dateEnd) : '';
  if (!dateStart) return finStr || undefined;
  const debutStr = formatDiffereHistoryDate(dateStart);
  if (debutStr === finStr) return finStr || undefined;
  if (debutStr && finStr) return `${debutStr} → ${finStr}`;
  return debutStr || finStr || undefined;
}

export function formatDiffereTypeLabel(differe?: TrainingHistoryContext['differe']): string | undefined {
  if (!differe) return undefined;
  if (differe.statsDrill) return 'Stats différé';
  if (differe.subType) {
    return DIFFERE_SUB_TYPE_LABELS[differe.subType] ?? differe.subType;
  }
  if (differe.differeType) {
    return DIFFERE_CATEGORY_LABELS[differe.differeType] ?? differe.differeType;
  }
  return undefined;
}

export function canOpenDiffereSource(entry: TrainingHistoryEntry): boolean {
  const d = entry.context?.differe ?? entry.loadRequest.historyContext?.differe;
  if (!d || d.statsDrill) return false;
  return d.differeId != null
    && d.dealNum != null
    && !!d.differeType
    && (d.partieId != null || d.pseudo != null);
}

export function buildDiffereNavigationRequest(
  entry: TrainingHistoryEntry,
): import('../types/app').DiffereNavigationRequest | null {
  const d = entry.context?.differe ?? entry.loadRequest.historyContext?.differe;
  if (!canOpenDiffereSource(entry) || !d) return null;
  const diffType = d.differeType as 'indiv' | 'tripl' | 'quad';
  if (diffType !== 'indiv' && diffType !== 'tripl' && diffType !== 'quad') return null;
  return {
    diffType,
    differeId: Number(d.differeId),
    playerPseudo: d.pseudo,
    playerDisplay: d.pseudoDisplay,
    dealNum: d.dealNum,
    partieId: d.partieId,
    view: 'replay',
  };
}

export const LIBRARY_ORIGIN_LABELS: Record<string, string> = {
  libre: 'Libre',
  differe_indiv: 'Diff. indiv.',
  differe_tq: 'Diff. TQ',
  defi: 'Défi',
  training: 'Entraînement',
};

export interface TrainingHistoryLoadRequest {
  libraryGameId?: number | null;
  libraryGameSource?: 'own' | 'shared';
  commentsEnabled?: boolean;
  gameData: unknown;
  mode: 'replay' | 'play';
  position?: number;
  biddingMode?: 'auto' | 'free';
  isPartial?: boolean;
  partialPhase?: number;
  partialTrickNum?: number;
  replayAllBots?: boolean;
  replaySeatLabels?: string[];
  pollVoteMode?: boolean;
  historyContext?: TrainingHistoryContext;
  compareReplayBatch?: {
    title?: string;
    deals?: CompareReplayDealEntry[];
    selectedIndex?: number;
    selectedVariantIndex?: number;
  };
  /** Plis 1-based à marquer (bifurcations exercice). */
  bifurcationTricks?: number[];
}

export interface TrainingHistorySettings {
  displayMode: TrainingDisplayMode;
  showTimeline: boolean;
  disabledBots: number[];
  humanAutomated: boolean;
  gameSpeed: TrainingHistoryGameSpeed;
  aiModelSelection: TrainingAIModelSelection;
  activePosition: number;
}

export interface TrainingHistoryEntry {
  id: string;
  createdAt: number;
  updatedAt: number;
  status: TrainingHistoryStatus;
  title: string;
  subtitle?: string;
  source: TrainingHistorySource;
  context?: TrainingHistoryContext;
  loadRequest: TrainingHistoryLoadRequest;
  settings?: TrainingHistorySettings;
  pollMode?: {
    sharedGameId: number;
    sharedBy: string;
    partialPhase?: number;
    voteSeat?: number;
  };
}

export interface TrainingHistoryDisplay {
  title: string;
  subtitle: string;
  sourceTag?: string;
  /** Avatars des deux équipes (championnat par équipes). */
  teams?: Array<{ nom: string; id: number }>;
  /** Date du match formatée (si connue). */
  matchDateLabel?: string;
}

export function getTrainingHistoryStorageKey(pseudo: string): string {
  return `${STORAGE_KEY_PREFIX}:${pseudo.toLowerCase()}`;
}

export function createTrainingHistoryId(): string {
  return `th_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function readAll(pseudo: string): TrainingHistoryEntry[] {
  try {
    const raw = localStorage.getItem(getTrainingHistoryStorageKey(pseudo));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(pseudo: string, entries: TrainingHistoryEntry[]): void {
  const sorted = [...entries].sort((a, b) => b.updatedAt - a.updatedAt);
  let trimmed = sorted;
  if (trimmed.length > MAX_ENTRIES) {
    const inProgress = trimmed.filter((e) => e.status === 'in_progress');
    const finished = trimmed.filter((e) => e.status === 'finished');
    const keepFinished = Math.max(0, MAX_ENTRIES - inProgress.length);
    trimmed = [...inProgress, ...finished.slice(0, keepFinished)];
  }
  localStorage.setItem(getTrainingHistoryStorageKey(pseudo), JSON.stringify(trimmed));
}

export function listTrainingHistory(
  pseudo: string,
  options?: { filter?: TrainingHistoryFilter },
): TrainingHistoryEntry[] {
  const filter = options?.filter ?? 'in_progress';
  const entries = readAll(pseudo).sort((a, b) => b.updatedAt - a.updatedAt);
  return entries.filter((e) => e.status === filter);
}

export function countTrainingHistory(
  pseudo: string,
  filter: TrainingHistoryFilter,
): number {
  return readAll(pseudo).filter((e) => e.status === filter).length;
}

export function upsertTrainingHistoryEntry(
  pseudo: string,
  entry: TrainingHistoryEntry,
): TrainingHistoryEntry[] {
  const entries = readAll(pseudo);
  const idx = entries.findIndex((e) => e.id === entry.id);
  const next = [...entries];
  if (idx >= 0) {
    next[idx] = entry;
  } else {
    next.unshift(entry);
  }
  writeAll(pseudo, next);
  return readAll(pseudo);
}

export function deleteTrainingHistoryEntry(pseudo: string, id: string): TrainingHistoryEntry[] {
  const entries = readAll(pseudo).filter((e) => e.id !== id);
  writeAll(pseudo, entries);
  return readAll(pseudo);
}

export function bidLabelShort(bid: number): string {
  switch (bid) {
    case 0: return 'Passe';
    case 1: return 'Petite';
    case 2: return 'Garde';
    case 3: return 'Garde sans';
    case 4: return 'Garde contre';
    default: return String(bid);
  }
}

export function getPartialPhaseLabel(
  partialPhase?: number,
  partialTrickNum?: number,
): string {
  if (partialPhase === 1) return 'Enchères';
  if (partialPhase === 2) return 'Écart';
  if (partialTrickNum && partialTrickNum > 0) return `Pli ${partialTrickNum}`;
  return 'En jeu';
}

export function formatTrainingHistoryDate(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const isToday =
    d.getDate() === now.getDate()
    && d.getMonth() === now.getMonth()
    && d.getFullYear() === now.getFullYear();
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Aujourd'hui ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    d.getDate() === yesterday.getDate()
    && d.getMonth() === yesterday.getMonth()
    && d.getFullYear() === yesterday.getFullYear();
  if (isYesterday) return `Hier ${time}`;
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface TrainingHistoryTitleInput {
  status: TrainingHistoryStatus;
  source: TrainingHistorySource;
  phase: string;
  highestBid: number;
  scores?: { won?: boolean; takerPoints?: number } | null;
  compareReplayBatch?: {
    title: string;
    deals: Array<{ label: string; variants?: unknown[] }>;
    selectedIndex: number;
    selectedVariantIndex: number;
  } | null;
}

export function buildTrainingHistoryTitle(input: TrainingHistoryTitleInput): string {
  const { status, source, phase, highestBid, scores, compareReplayBatch } = input;

  if (compareReplayBatch && compareReplayBatch.deals.length > 0) {
    const deal = compareReplayBatch.deals[compareReplayBatch.selectedIndex];
    const dealLabel = deal?.label ?? `Donne ${compareReplayBatch.selectedIndex + 1}`;
    const batchTitle = compareReplayBatch.title || 'Comparateur';
    if ((deal?.variants?.length ?? 0) > 1) {
      const tableNum = compareReplayBatch.selectedVariantIndex + 1;
      return `${batchTitle} — ${dealLabel} (table ${tableNum})`;
    }
    if (compareReplayBatch.deals.length > 1) {
      return `${batchTitle} — ${dealLabel}`;
    }
    return batchTitle;
  }

  if (status === 'finished' && scores) {
    const contract = highestBid > 0 ? bidLabelShort(highestBid) : 'Donne';
    const result = scores.won ? 'réussie' : 'chutée';
    const pts = scores.takerPoints != null ? ` (${scores.takerPoints} pts)` : '';
    return `${contract} ${result}${pts}`;
  }

  if (source === 'library') return 'Bibliothèque';
  if (source === 'poll') return 'Sondage';
  if (source === 'differe') return 'Différé';
  if (source === 'cia') return 'Défi CIA';

  if (phase === 'bidding') return 'Entraînement — Enchères';
  if (phase === 'ecart') return 'Entraînement — Écart';
  if (phase === 'playing') return 'Entraînement — En jeu';
  if (phase === 'end_of_deal') return 'Entraînement — Fin de donne';

  return 'Entraînement';
}

function buildProgressParts(entry: TrainingHistoryEntry): string[] {
  const lr = entry.loadRequest;
  const parts: string[] = [];

  if (entry.status === 'finished') parts.push('Terminée');
  else parts.push('En cours');

  if (lr.isPartial) {
    parts.push(getPartialPhaseLabel(lr.partialPhase, lr.partialTrickNum));
  }

  parts.push(lr.mode === 'replay' ? 'Replay' : 'Jeu');

  const batch = lr.compareReplayBatch;
  if (batch?.deals && batch.deals.length > 1) {
    const idx = batch.selectedIndex ?? 0;
    parts.push(`${idx + 1}/${batch.deals.length} donnes`);
  } else if (batch?.deals?.some((d) => (d.variants?.length ?? 0) > 1)) {
    const vIdx = batch.selectedVariantIndex ?? 0;
    parts.push(`table ${vIdx + 1}`);
  }

  return parts;
}

function normHistoryPseudo(p: string): string {
  return p.toLowerCase().replace(/¶/g, ' ').trim();
}

export function formatCiaPlayersLabel(
  cia: NonNullable<TrainingHistoryContext['cia']>,
): string | undefined {
  const viewer = cia.viewerPseudo ? normHistoryPseudo(cia.viewerPseudo) : '';
  const p1Pseudo = cia.player1Pseudo ? normHistoryPseudo(cia.player1Pseudo) : '';
  const p2Pseudo = cia.player2Pseudo ? normHistoryPseudo(cia.player2Pseudo) : '';
  const viewerPlayed = viewer && (viewer === p1Pseudo || viewer === p2Pseudo);

  if (viewerPlayed) {
    const opponent = cia.opponentDisplay
      ?? (viewer === p1Pseudo ? cia.player2Display : cia.player1Display)
      ?? (viewer === p1Pseudo ? cia.player2Pseudo : cia.player1Pseudo);
    return opponent ? `vs ${opponent}` : undefined;
  }

  const p1 = cia.player1Display || cia.player1Pseudo;
  const p2 = cia.player2Display || cia.player2Pseudo;
  if (p1 && p2) return `${p1} vs ${p2}`;
  if (cia.opponentDisplay) return `vs ${cia.opponentDisplay}`;
  return undefined;
}

function buildContextParts(ctx: TrainingHistoryContext | undefined): string[] {
  if (!ctx) return [];
  const parts: string[] = [];

  if (ctx.kind === 'differe' && ctx.differe) {
    const d = ctx.differe;
    if (d.pseudoDisplay || d.pseudo) parts.push(d.pseudoDisplay || d.pseudo!);
    if (d.dealNum != null) parts.push(`donne ${d.dealNum}`);
  } else if (ctx.kind === 'library' && ctx.library) {
    const l = ctx.library;
    const originLabel = LIBRARY_ORIGIN_LABELS[l.originType ?? ''] ?? l.originType;
    if (originLabel) parts.push(originLabel);
    if (l.originDonneNum != null) parts.push(`donne #${l.originDonneNum}`);
    if (l.contractLabel) parts.push(l.contractLabel);
    if (l.note) {
      const note = l.note.trim();
      parts.push(note.length > 48 ? `${note.slice(0, 48)}…` : note);
    }
    if (l.source === 'shared' && l.sharedBy) parts.push(`partagée par ${l.sharedBy}`);
    else if (l.gameId) parts.push(`#${l.gameId}`);
  } else if (ctx.kind === 'cia' && ctx.cia) {
    const playersLabel = formatCiaPlayersLabel(ctx.cia);
    if (playersLabel) parts.push(playersLabel);
    if (ctx.cia.dealNum != null) parts.push(`donne ${ctx.cia.dealNum}`);
    if (ctx.cia.tableIdx != null) parts.push(`table ${ctx.cia.tableIdx}`);
  } else if (ctx.kind === 'team_champ' && ctx.teamChamp) {
    const t = ctx.teamChamp;
    if (t.dealNum != null) parts.push(`donne ${t.dealNum}`);
    if (t.tableIdx != null) parts.push(`table ${t.tableIdx}`);
  } else if (ctx.kind === 'compare' && ctx.compare) {
    if (ctx.compare.modelLabel) parts.push(ctx.compare.modelLabel);
    if (ctx.compare.metricLabel) parts.push(ctx.compare.metricLabel);
    if (ctx.compare.dealCount != null && ctx.compare.selectedDealIndex != null) {
      parts.push(`donne ${ctx.compare.selectedDealIndex + 1}/${ctx.compare.dealCount}`);
    }
    if (ctx.compare.tableIdx != null) parts.push(`table ${ctx.compare.tableIdx}`);
  } else if (ctx.kind === 'poll' && ctx.poll) {
    if (ctx.poll.sharedBy) parts.push(`par ${ctx.poll.sharedBy}`);
  }

  return parts;
}

function resolveSourceTag(ctx: TrainingHistoryContext | undefined, source: TrainingHistorySource): string | undefined {
  if (!ctx) {
    if (source === 'solo') return 'Entraînement libre';
    if (source === 'team_champ') return 'Champ. par éq.';
    if (source === 'cia') return 'Défi CIA';
    return undefined;
  }
  switch (ctx.kind) {
    case 'differe':
      return ctx.differe?.statsDrill ? 'Stats différé' : 'Différé';
    case 'library':
      return ctx.library?.source === 'shared' ? 'Bibliothèque (partagée)' : 'Bibliothèque';
    case 'cia':
      return 'Défi CIA';
    case 'team_champ':
      return 'Champ. par éq.';
    case 'compare':
      return 'Comparateur ML';
    case 'poll':
      return 'Sondage';
    case 'solo':
      return 'Entraînement libre';
    default:
      if (source === 'team_champ') return 'Champ. par éq.';
      return undefined;
  }
}

function resolveContextTitle(
  ctx: TrainingHistoryContext | undefined,
  fallbackTitle: string,
): string {
  if (!ctx) return fallbackTitle;

  switch (ctx.kind) {
    case 'differe': {
      const d = ctx.differe;
      if (d?.statsDrill) return 'Stats différé';
      const typeLabel = formatDiffereTypeLabel(d);
      const dates = formatDiffereDateRange(d?.dateStart, d?.dateEnd);
      if (typeLabel && dates) return `${typeLabel} · ${dates}`;
      if (typeLabel) return typeLabel;
      if (dates) return dates;
      return 'Différé';
    }
    case 'library':
      return 'Bibliothèque';
    case 'cia':
      return 'Défi CIA';
    case 'team_champ': {
      const t = ctx.teamChamp;
      if (t?.equipe1Nom && t?.equipe2Nom) return `${t.equipe1Nom} vs ${t.equipe2Nom}`;
      return fallbackTitle || 'Championnat par équipes';
    }
    case 'compare':
      return ctx.compare?.metricLabel || ctx.compare?.modelLabel || fallbackTitle;
    case 'poll':
      return 'Sondage';
    case 'solo':
      return fallbackTitle;
    default:
      return fallbackTitle;
  }
}

function resolveTeamChampTeams(
  ctx: TrainingHistoryContext | undefined,
): Array<{ nom: string; id: number }> | undefined {
  const t = ctx?.teamChamp;
  if (!t?.equipe1Nom || !t?.equipe2Nom) return undefined;
  return [
    { nom: t.equipe1Nom, id: t.equipe1Id != null ? Number(t.equipe1Id) : 1 },
    { nom: t.equipe2Nom, id: t.equipe2Id != null ? Number(t.equipe2Id) : 2 },
  ];
}

export function formatTrainingHistoryDisplay(entry: TrainingHistoryEntry): TrainingHistoryDisplay {
  const fallbackTitle = entry.title;
  const ctx = entry.context ?? entry.loadRequest.historyContext;
  const title = resolveContextTitle(ctx, fallbackTitle);
  const contextParts = buildContextParts(ctx);
  const progressParts = buildProgressParts(entry);

  const subtitleParts = contextParts.length > 0
    ? [...contextParts, ...progressParts.filter((p) => !contextParts.includes(p))]
    : progressParts;

  const matchDateLabel = ctx?.kind === 'team_champ' && ctx.teamChamp?.matchDate
    ? formatDiffereHistoryDate(ctx.teamChamp.matchDate) || undefined
    : undefined;

  return {
    title,
    subtitle: subtitleParts.join(' · '),
    sourceTag: resolveSourceTag(ctx, entry.source),
    teams: ctx?.kind === 'team_champ' ? resolveTeamChampTeams(ctx) : undefined,
    matchDateLabel,
  };
}

export function enrichHistoryContext(
  base: TrainingHistoryContext | undefined,
  options: {
    compareReplayBatch?: {
      deals: Array<{ dealIndex?: number; partieId?: number; variants?: Array<{ tableIdx?: number }> }>;
      selectedIndex: number;
      selectedVariantIndex: number;
    } | null;
    pollMode?: { sharedGameId: number; sharedBy: string } | null;
    libraryGameId?: number | null;
    libraryGameSource?: 'own' | 'shared';
  },
): TrainingHistoryContext | undefined {
  if (!base && !options.compareReplayBatch && !options.pollMode && !options.libraryGameId) {
    return undefined;
  }

  const ctx: TrainingHistoryContext = base
    ? {
        ...base,
        library: base.library ? { ...base.library } : undefined,
        differe: base.differe ? { ...base.differe } : undefined,
        cia: base.cia ? { ...base.cia } : undefined,
        teamChamp: base.teamChamp ? { ...base.teamChamp } : undefined,
        compare: base.compare ? { ...base.compare } : undefined,
        poll: base.poll ? { ...base.poll } : undefined,
      }
    : { kind: 'solo' };

  if (options.libraryGameId != null && !ctx.library) {
    ctx.kind = 'library';
    ctx.library = {
      gameId: options.libraryGameId,
      source: options.libraryGameSource ?? 'own',
    };
  }

  if (options.pollMode) {
    ctx.kind = 'poll';
    ctx.poll = {
      sharedGameId: options.pollMode.sharedGameId,
      sharedBy: options.pollMode.sharedBy,
    };
  }

  const batch = options.compareReplayBatch;
  if (batch && batch.deals.length > 0) {
    const deal = batch.deals[batch.selectedIndex];
    const variant = deal?.variants?.[batch.selectedVariantIndex];

    if (ctx.kind === 'cia' && ctx.cia) {
      ctx.cia.dealNum = deal?.dealIndex ?? ctx.cia.dealNum;
      ctx.cia.tableIdx = variant?.tableIdx ?? ctx.cia.tableIdx;
    } else if (ctx.kind === 'team_champ' && ctx.teamChamp) {
      ctx.teamChamp.dealNum = deal?.dealIndex ?? ctx.teamChamp.dealNum;
      ctx.teamChamp.tableIdx = variant?.tableIdx ?? ctx.teamChamp.tableIdx;
    } else if (ctx.kind === 'compare' && ctx.compare) {
      ctx.compare.selectedDealIndex = batch.selectedIndex;
      ctx.compare.dealCount = batch.deals.length;
      ctx.compare.tableIdx = variant?.tableIdx ?? ctx.compare.tableIdx;
    } else if (ctx.kind === 'differe' && ctx.differe) {
      const dealNum = deal?.dealIndex != null ? deal.dealIndex : undefined;
      if (dealNum != null) ctx.differe.dealNum = dealNum;
      if (deal?.partieId != null) ctx.differe.partieId = deal.partieId;
    }
  }

  return ctx;
}
